import { format } from "date-fns";
import { DataTable, DataTableColumnDef } from "@/components/molecules";
import { useProfile } from "@/hooks";
import { BookingPaymentBreakdownLog } from "@/redux/slices/booking/types";
import { formatCurrency } from "@/utils";
import { useSelectedBooking } from "../../../hooks/useSelectedBooking";
import { PaymentStatus } from "../PaymentStatus";
import { getPaidOutPaymentType, getPaidOutFromToText } from "../helpers";
import { PaidOutData } from "../types";

const paidOutColumns: DataTableColumnDef<PaidOutData>[] = [
  {
    header: "Payment Date",
    cell: (props) => <span className={props.row.original.id === "total" ? " text-primary" : ""}>{props.row.original.paymentDate}</span>,
    size: 120,
    enableSorting: false,
  },
  {
    header: "Amount Paid",
    cell: (props) => <span className={props.row.original.id === "total" ? " text-primary" : ""}>{props.row.original.amountPaid}</span>,
    size: 100,
    enableSorting: false,
  },
  {
    header: "Payment Type",
    accessorKey: "paymentType",
    size: 200,
    enableSorting: false,
  },
  {
    header: "From → To",
    accessorKey: "fromTo",
    size: 200,
    enableSorting: false,
  },
  {
    header: "Status",
    cell: (props) => props.row.original.status,
    enableSorting: false,
  },
];

export const PaidOutTable = ({ items }: { items: BookingPaymentBreakdownLog[] }) => {
  const { dateFormat } = useProfile();
  const {
    cost: { currency },
    driver,
    operator,
  } = useSelectedBooking();
  const isOffloaded = operator.providerKey !== driver?.operator?.providerKey;

  const data: PaidOutData[] = [...items]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map((item) => {
      const isReversal = item.transferType.includes("reversal");
      const amount = isReversal ? -Math.abs(item.amount) : item.amount;

      return {
        id: item.id,
        paymentDate: format(new Date(item.createdAt), dateFormat),
        amountPaid: formatCurrency(amount, 2, currency),
        paymentType: getPaidOutPaymentType(item.transferType),
        fromTo: getPaidOutFromToText(item.category, item.transferType, operator.name, isOffloaded ? driver?.operator?.name : undefined),
        status: (
          <PaymentStatus
            status={item.status}
            customLabels={{
              paid: "Complete",
            }}
          />
        ),
      };
    });

  const totalAmount = items.reduce((sum, item) => {
    const isAdjustment = item.transferType.includes("reversal");
    const amount = isAdjustment ? -Math.abs(item.amount) : item.amount;
    return sum + amount;
  }, 0);

  const totalRow: PaidOutData = {
    id: "total",
    paymentDate: "Total:",
    amountPaid: formatCurrency(totalAmount, 2, currency),
    paymentType: "",
    fromTo: "",
    status: <span></span>,
  };

  const dataWithTotal = [...data, totalRow];

  return <DataTable data={dataWithTotal} columns={paidOutColumns} className="mt-5 w-full overflow-x-auto [&_table]:table-auto" />;
};
